import { useCallback, useEffect, useRef } from "react";
import { showDailyLimitPaywall, type PaywallMoment } from "@/hooks/usePaywallDialog";
import { COPY_BLOCKED_MESSAGE } from "@/lib/copyText";
import { useUsageGate } from "@/hooks/useUsageGate";
import { clearResultGate, setResultGate } from "@/lib/resultGate";
import { getCurrentUsage, isToolSessionCounted } from "@/lib/usageGate";

/**
 * For live tools (calculators, counters, converters that update as you
 * type). The result is always shown -- reading an answer is never blocked or
 * charged. What costs a trial use is TAKING the output away: copying it,
 * printing it or saving it. (Owner decision, 24 Oct 2026: before this the
 * first edit charged a use, which QA flagged as the most damaging case for
 * trust -- charging for a unit conversion or a tax sum.)
 *
 * How the charge is applied without touching every tool:
 *  - ToolContainer shows a "Copy result / Print / Save" bar for tools that
 *    don't have their own output buttons; its buttons call `gate()`;
 *  - while the tool is open, navigator.clipboard.writeText and a copy of
 *    selected result text are gated too, so a tool's own Copy buttons and a
 *    plain Ctrl+C are covered. Selecting inside an editable field (the
 *    person's own input) is not output and is never charged;
 *  - one use per tool visit (requestToolSessionUse): further copies, prints
 *    and saves in the same visit cost nothing more;
 *  - when the daily limit is up the action is cancelled and the paywall
 *    opens. The result itself stays on screen.
 *
 * `blocked` is kept in the return value so existing call sites compile; it
 * is always false now. The second argument (an old debounce) does nothing.
 */
export function useLogToolUsage(
  hasResult: unknown,
  _debounceMs?: number,
  opts?: { ownActions?: boolean },
): { blocked: boolean; gate: (moment?: PaywallMoment) => Promise<boolean> } {
  const { known, tryUse, paid, toolId, scope } = useUsageGate();
  const ref = useRef({ known, tryUse, toolId, scope, paid, hasResult: !!hasResult });
  ref.current = { known, tryUse, toolId, scope, paid, hasResult: !!hasResult };

  /** Resolves true when the output may leave the page. */
  const gate = useCallback(async (moment: PaywallMoment = "download"): Promise<boolean> => {
    const c = ref.current;
    if (!c.known || !c.hasResult) return true;
    const decision = await c.tryUse();
    if (decision === "allowed") return true;
    showDailyLimitPaywall(c.toolId, moment);
    return false;
  }, []);

  // Make the gate and result state available to ToolContainer's bar.
  const ownActions = !!opts?.ownActions;
  useEffect(() => {
    setResultGate({ active: known, hasResult: !!hasResult, ownActions, paid, gate });
  }, [known, hasResult, ownActions, paid, gate]);
  useEffect(() => () => clearResultGate(), []);

  // Gate the clipboard while this tool is open.
  useEffect(() => {
    if (!known) return;
    const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    const original = clip?.writeText?.bind(clip);
    if (clip && original) {
      try {
        clip.writeText = async (text: string) => {
          if (!(await gate("copy"))) throw new DOMException(COPY_BLOCKED_MESSAGE, "NotAllowedError");
          return original(text);
        };
      } catch { /* read-only clipboard object: leave it alone */ }
    }
    const onCopy = (e: ClipboardEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest || !target.closest("main")) return;
      // Copying from something the person typed is not taking output away.
      const field = target.closest("input, textarea") as HTMLInputElement | HTMLTextAreaElement | null;
      if (field && !field.readOnly) return;
      if (!ref.current.hasResult) return;
      if (!window.getSelection()?.toString()) return;
      // A copy can't wait for a server answer, so when the cached count already
      // says the limit is up, cancel it on the spot and open the paywall.
      const c = ref.current;
      const snap = getCurrentUsage();
      if (!c.paid && !isToolSessionCounted(c.toolId) && snap && snap.scope === c.scope && !snap.allowed) {
        e.preventDefault();
        showDailyLimitPaywall(c.toolId, "copy");
        return;
      }
      void gate("copy");
    };
    document.addEventListener("copy", onCopy, true);
    return () => {
      document.removeEventListener("copy", onCopy, true);
      if (clip && original) {
        try { clip.writeText = original; } catch { /* ignore */ }
      }
    };
  }, [known, gate]);

  return { blocked: false, gate };
}
